import { getCookie } from 'cookies-next';
import moment from 'moment';
import { formatDateMoment, formatPhoneNumberForDisplay, getSiteDivisionCode } from '@/helpers/utils/siteUtils';
import {
  COOKIE_KEYS,
  DATE_FORMAT,
  SEARCH_CUSTOMER_PAGE_PATH,
  SEARCH_ORDER_PAGE_PATH,
  SITE_KEY_MAP,
  SITE_KEY_MAP_MO,
} from '../../../../../constants/constant';
import { TableColumnConfig } from '../../ui/customTable/Table';
import TableColumns from '../components/common/table/columns';
import { CustomerSearchInputRecord, CustomerSearchTransformedRecord, ProcessedOrder, Order } from '../types';

export function TransformCustomerSearchRecords(
  records: CustomerSearchInputRecord[],
): CustomerSearchTransformedRecord[] {
  return records.map((record): CustomerSearchTransformedRecord => {
    const firstDivision = record?.DivisionCustomers?.[0];
    const emailAddress = firstDivision?.EmailRecords?.[0]?.EmailInfo?.emailAddress ?? '';
    const phoneNumber = firstDivision?.PhoneRecords?.[0]?.PhoneInfo?.phoneNumber
      ? formatPhoneNumberForDisplay(firstDivision?.PhoneRecords?.[0]?.PhoneInfo?.phoneNumber)
      : '';

    return {
      accountNumber: record?.CustomerInfo?.omsCustomerId ?? '',
      divisions: {
        divisionsData:
          record?.CustomerInfo?.AffiliatedDivisions?.filter((item) => {
            return SITE_KEY_MAP_MO?.[item];
          }) ?? [],
        accountNumber: record?.CustomerInfo?.omsCustomerId ?? '',
      },
      recordType: record?.recordType,
      divRecordType: firstDivision?.DivisionCustomerInfo?.divRecordType,
      mailingRecordId: firstDivision?.CurrentPromotionInfo?.BestMailingRecord?.mailingRecordId,
      lastName: record?.CustomerInfo?.lastName ?? '',
      firstName: record?.CustomerInfo?.firstName ?? '',
      companyName: record?.CustomerInfo?.companyName ?? '',
      dateOfBirth: record?.CustomerInfo?.dob ? moment(record.CustomerInfo.dob).format(DATE_FORMAT) : '',
      ssn: record?.CustomerInfo?.ssn ? `XXX-XX-${record?.CustomerInfo?.ssn}` : '',
      addressLine1: record?.BillingAddress?.LocationInfo?.line1 ?? '',
      addressLine2: record?.BillingAddress?.LocationInfo?.line2 ?? '',
      city: record?.BillingAddress?.LocationInfo?.city ?? '',
      state: record?.BillingAddress?.LocationInfo?.state ?? '',
      zipCode: record?.BillingAddress?.LocationInfo?.zip ?? '',
      emailAddress,
      phoneNumber,
      customerId: record?.customerId,
      masterCustomerData: record,
    };
  });
}

export function TransformOrderSearchRecords(orders: Order[], selectedSearchType: string): ProcessedOrder[] {
  const currentSite = getCookie(COOKIE_KEYS.selectedSite) ?? '';
  const currentSiteId = getSiteDivisionCode(currentSite);

  if (selectedSearchType != 'SearchOrderQueues') {
    orders = orders?.filter((order) => order?.OrderInfo?.divisionCode === currentSiteId);
  }
  return orders?.map((order) => {
    const omsCustomerId = order?.OrderInfo?.omsCustomerId;
    const orderId = order?.OrderInfo?.orderId ?? order?.orderId ?? '';
    const orderInfo = order?.OrderInfo || {};
    const billingInfo = order?.BillingAddress?.PersonInfo || {};
    const locationInfo =
      order?.BillingAddress?.LocationInfo || order?.Recipients?.[0]?.Address?.[0]?.LocationInfo || {};
    const recipientInfo = order?.Recipients?.[0]?.Address?.[0]?.PersonInfo || {};
    let shipments = order?.Recipients?.flatMap((recepient) => recepient.Shipments);
    shipments = shipments?.length ? shipments : [];
    const lineItems = order?.Recipients?.flatMap((recepient) => recepient.LineItems);
    const filteredLineItems = lineItems?.filter((lineItem) => !lineItem?.LineItemInfo?.isShippingInsurancePolicy) || [];
    const trackingCodes = shipments.flatMap(
      (shipment) =>
        shipment?.TrackingDetails?.map((detail) => {
          return {
            trackingCode: detail?.TrackingInfo?.trackingCode ?? '',
            trackingLink: detail?.TrackingInfo?.trackingLink ?? '',
            externalTrackingLink: detail?.TrackingInfo?.externalTrackingLink ?? '',
          };
        }) ?? [],
    );

    const status = orderInfo?.statusDescription ?? '' + (trackingCodes ? `:${trackingCodes}` : '');
    const statusWithTracking = {
      statusText: status,
      trackingInfo: trackingCodes,
    };

    let paymentMethod =
      order.Payments?.[0]?.PaymentInfo?.paymentType ?? order?.Payments?.[0]?.PaymentInfo?.termsDescription ?? '';
    if (
      orderInfo.divisionCode === 'ZD' &&
      (paymentMethod === 'MasonCredit' ||
        paymentMethod === 'Easy Pay Payment Option' ||
        paymentMethod === 'Mason Credit')
    ) {
      paymentMethod = 'Mason Easy-Pay';
    }
    let orderTerms = order?.Payments?.[0]?.PaymentInfo?.termsDescription ?? '';
    if (
      orderInfo.divisionCode === 'ZD' &&
      (orderTerms === 'MasonCredit' || orderTerms === 'Easy Pay Payment Option' || orderTerms === 'Mason Credit')
    ) {
      orderTerms = 'Mason Easy-Pay';
    }
    const customerName =
      `${billingInfo?.firstName ?? recipientInfo?.firstName ?? ''} ${
        billingInfo?.lastName ?? recipientInfo?.lastName ?? ''
      }`.trim() || '';

    const addressParts = [
      locationInfo?.line1,
      locationInfo?.city,
      locationInfo?.state,
      locationInfo?.zip,
      locationInfo?.country,
    ]
      ?.filter(Boolean)
      ?.join(', ');
    const orderAmount =
      order?.PricingInfo?.totalAmt ??
      (typeof order?.PricingInfo?.EstimatedTotal === 'object'
        ? order?.PricingInfo?.EstimatedTotal.totalAmt
        : order?.PricingInfo?.EstimatedTotal) ??
      '';
    const division =
      orderInfo && orderInfo.divisionCode && SITE_KEY_MAP[orderInfo.divisionCode]
        ? SITE_KEY_MAP[orderInfo.divisionCode]
        : '';
    return {
      orderId,
      status: statusWithTracking,
      total: orderAmount,
      numberOfItems: filteredLineItems?.length ?? '',
      paymentMethod,
      lastModified: formatDateMoment(orderInfo?.updatedOn ?? '', DATE_FORMAT),
      origin: orderInfo?.medium ?? '',
      customerName,
      address: addressParts || '',
      lastName: billingInfo?.lastName ?? '',
      firstName: billingInfo?.firstName ?? '',
      orderDate: moment(orderInfo?.submittedOn)?.format(DATE_FORMAT) ?? '',
      division: division,
      orderStatus: orderInfo?.statusDescription ?? '',
      orderAmount: orderAmount,
      channel: orderInfo?.createdBy ?? '',
      lastAttemptDate: orderInfo?.updatedOn ?? '',
      orderTerms: orderTerms,
      omsCustomerId,
    };
  });
}

export const GetTableData = ({
  pagePath,
  pageData,
  Texts,
  selectedSearchType,
  queueName,
  setAllRowsDataFuncRef,
  setAllRowsDataRef,
}: {
  pagePath: string;
  pageData: any;
  Texts: any; //need to change this any to proper type
  selectedSearchType?: string;
  queueName?: any; //need to change this any to proper type
  setAllRowsDataFuncRef?: any;
  setAllRowsDataRef?: any;
}) => {
  let columnsData: TableColumnConfig<any>[] = [];

  let allRowsData: any[] = [];
  if (pagePath === SEARCH_CUSTOMER_PAGE_PATH) {
    if (setAllRowsDataRef?.current) allRowsData = setAllRowsDataRef?.current;
    //this is done for adding dynamic changes to the table records after adding division
    else allRowsData = TransformCustomerSearchRecords(pageData?.Customers || []) as any;
    columnsData = TableColumns({ Texts, selectedSearchType, pagePath, allRowsData, setAllRowsDataFuncRef }) as any;
  }
  if (pagePath === SEARCH_ORDER_PAGE_PATH) {
    allRowsData = TransformOrderSearchRecords(
      pageData?.Orders || (pageData?.Order ? [pageData?.Order] : []),
      selectedSearchType || '',
    ) as any;
    columnsData = TableColumns({ Texts, selectedSearchType, pagePath, allRowsData, queueName }) as any;
  }
  return {
    allRowsData,
    columnsData,
  };
};
